import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { SxProps, Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";

import theme from "@theme/index";

export interface ISlideNavButtonsProperties {
  backButtonText?: string;
  backButtonTooltip?: string;
  handleBack: () => void;
  handleNext: () => void;
  isBackButtonDisabled?: boolean;
  isBackButtonPending?: boolean;
  isNextButtonDisabled?: boolean;
  isNextButtonPending?: boolean;
  nextButtonStyle?: SxProps<Theme>;
  nextButtonText?: string;
  nextButtonTooltip?: string;
  showNextButton?: boolean;
}

/**
 * Navigation buttons for use in a slide app bar. The UI can be controlled using the various props
 *
 * @param nextButtonText - The text for the Next button.
 * @param isNextButtonDisabled - Indicator for disabling the Next button.
 * @param nextButtonTooltip - The tooltip for the Next button.
 * @param isNextButtonPending - If true, renders a circular progress component inside the next button.
 * @param showNextButton - If true, renders the Next button.
 * @param isBackButtonDisabled - Indicator for disabling the Back button.
 * @param backButtonTooltip - The tooltip for the Back button.
 * @param backButtonText - The text for the Back button.
 * @param isBackButtonPending - If true, renders a circular progress component inside the back button.
 * @param handleBack - The function to handle the Back button click.
 * @param handleNext - The function to handle the Next button click.
 * @param nextButtonStyle - The style for the Next button.
 */
export default function SlideNavButtons({
  backButtonText = "Back",
  backButtonTooltip = "",
  handleBack,
  handleNext,
  isBackButtonDisabled = false,
  isBackButtonPending = false,
  isNextButtonDisabled = false,
  isNextButtonPending = false,
  nextButtonStyle = {},
  nextButtonText = "Next",
  nextButtonTooltip = "",
  showNextButton = true,
}: ISlideNavButtonsProperties) {
  return (
    <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "nowrap" }}>
      {!isBackButtonDisabled && (
        <Tooltip placement="bottom" title={backButtonTooltip}>
          <span>
            <Button
              color="secondary"
              disabled={isBackButtonDisabled}
              onClick={handleBack}
              sx={{ borderRadius: "8px", mr: 3 }}
              variant="contained"
            >
              {isBackButtonPending ? (
                <CircularProgress size={20} sx={{ color: theme.palette.common.white }} />
              ) : (
                <>
                  <KeyboardArrowLeftRoundedIcon />
                  {backButtonText}
                </>
              )}
            </Button>
          </span>
        </Tooltip>
      )}
      {showNextButton && (
        <Tooltip placement="bottom" title={nextButtonTooltip}>
          <span>
            <Button
              className="get-contacts-next"
              color="secondary"
              disabled={isNextButtonDisabled}
              onClick={handleNext}
              sx={{
                alignItems: "center",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                ...nextButtonStyle,
              }}
              variant="contained"
            >
              {isNextButtonPending ? (
                <CircularProgress size={20} sx={{ color: theme.palette.common.white }} />
              ) : (
                <>
                  {nextButtonText}
                  {nextButtonText.includes("Export") ? (
                    <DownloadRoundedIcon sx={{ ml: 1 }} />
                  ) : (
                    <KeyboardArrowRightRoundedIcon />
                  )}
                </>
              )}
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
